import type { MonthlyMembershipDocument } from 'fundraising-contentful'
import { zMonthlyDocumentV1, type FormRowV1, type MonthlyDocumentV1 } from 'fundraising-core'
import { cloudinaryUrl, formLanding } from './donation'
import { htmlToText, sanitizeConsentHtml, sanitizeFormHtml } from './html'
import { KNOWN_REQUIRES } from './rules'
import { toResultPage, type ResultChoice, type ResultChoices } from './result'

/** A monthly document, or null for anything that isn't a valid v1 one (the caller falls back). */
export function parseMonthly(json: unknown): MonthlyDocumentV1 | null {
  let value = json
  if (typeof json === 'string') {
    try {
      value = JSON.parse(json)
    } catch {
      return null
    }
  }
  const parsed = zMonthlyDocumentV1.safeParse(value)
  return parsed.success ? parsed.data : null
}

/** `autogiro`: a consent e-signed with BankID. `paper`: a paper consent sent by post. */
export const KNOWN_MONTHLY_METHODS = ['autogiro', 'paper'] as const
export type MonthlyMethod = (typeof KNOWN_MONTHLY_METHODS)[number]

const isKnown = (method: string): method is MonthlyMethod =>
  (KNOWN_MONTHLY_METHODS as readonly string[]).includes(method)

export const knownMonthlyMethods = (methods: readonly string[]): MonthlyMethod[] => [
  ...new Set(methods.filter(isKnown)),
]

export const unknownMonthlyMethods = (methods: readonly string[]): string[] =>
  methods.filter((method) => !isKnown(method))

/** Why this reader can't serve the form, or '' when it can. Mirrors dr_forms MonthlyReaderRules. */
export function monthlyRefusalOf(form: MonthlyDocumentV1): string {
  const tokens = form.requires.filter((token) => !KNOWN_REQUIRES.includes(token))
  if (tokens.length) return `requires unknown feature(s): ${tokens.join(', ')}`
  const methods = knownMonthlyMethods(form.methods)
  if (!methods.length)
    return `no payment method this reader knows (${form.methods.join(', ') || 'none'})`
  const { done, pending, aborted } = form.results
  if (methods.includes('autogiro') && !((form.thanks || done) && (form.aborted || aborted)))
    return 'autogiro needs an e-signed page and an aborted page'
  // Unlike Gåva's postal, paper never falls back to the e-signed page.
  if (methods.includes('paper') && !(form.pending || pending))
    return 'paper needs a paper-consent page'
  return ''
}

/**
 * Which page each result state shows: `ok` after an e-signed consent, `postal` after a paper
 * consent, `aborted` after an aborted signing. The form's own page wins, then its Contentful
 * entry; paper has no fallback (MonthlyReaderRules), and the aborted page offers no quiz.
 */
export function monthlyResultChoices(form: MonthlyDocumentV1): ResultChoices {
  const { done, pending, aborted } = form.results
  const pick = (page: MonthlyDocumentV1['thanks'], id: string | null): ResultChoice =>
    page ? { page: toResultPage(page, form.next) } : id ? { sysId: id } : null
  const abortedChoice = pick(form.aborted, aborted)
  return {
    ok: pick(form.thanks, done),
    postal: pick(form.pending, pending),
    aborted:
      abortedChoice && 'page' in abortedChoice
        ? { page: { ...abortedChoice.page, quiz: null } }
        : abortedChoice,
  }
}

export interface MonthlyRowView {
  eyebrow: string
  title: string
  /** Sanitized rich text, lists in place. */
  bodyHtml: string
  /** A delivery URL, or '' for none. */
  image: string
  imageAlt: string
  link: string
  button: string
}

export interface MonthlyPresentView {
  /** The present's English key: the choice's value on the page, never sent to a CRM. */
  key: string
  label: string
  /** A delivery URL, or '' for none. */
  image: string
  imageAlt: string
  description: string
  /** What Mysoft records as the membership's Premieval. */
  mysoftCode: string
}

/** What a host's Månadsgivare page draws and posts, from a document. Browser-safe. */
export interface MonthlyView {
  formId: string
  lang: MonthlyDocumentV1['lang']
  slug: string
  title: string
  subtitle: string
  /** Sanitized rich text. */
  leadHtml: string
  /** The lead as plain text, for the meta description. */
  leadText: string
  /** A delivery URL, or '' for none. */
  image: string
  imageAlt: string
  rows: MonthlyRowView[]
  /** kr/mån, in display order. */
  sums: number[]
  defaultSum: number | null
  minSum: number | null
  /** The värvningskod; '' for none. */
  recruitmentCode: string
  methods: MonthlyMethod[]
  presents: MonthlyPresentView[]
  /** Sanitized; '' for none. */
  consentHtml: string
  isClosed: boolean
  noindex: boolean
}

const toRow = (row: FormRowV1): MonthlyRowView => ({
  eyebrow: row.eyebrow,
  title: row.heading,
  bodyHtml: sanitizeFormHtml(row.body),
  image: cloudinaryUrl(row.image),
  imageAlt: row.imageAlt || row.heading,
  link: row.link,
  button: row.button,
})

export function toMonthlyView(form: MonthlyDocumentV1): MonthlyView {
  const leadHtml = sanitizeFormHtml(form.lead)
  return {
    formId: form.id,
    lang: form.lang,
    slug: form.slug,
    title: form.heading,
    subtitle: form.eyebrow,
    leadHtml,
    leadText: htmlToText(leadHtml),
    image: cloudinaryUrl(form.heroImage),
    imageAlt: form.heroImageAlt || form.heading,
    rows: form.rows.map(toRow),
    sums: [...form.presets],
    defaultSum: form.defaultAmount,
    minSum: form.minimum,
    recruitmentCode: form.code,
    methods: knownMonthlyMethods(form.methods),
    presents: form.presents.map((present) => ({
      key: present.key,
      label: present.name,
      image: cloudinaryUrl(present.image),
      imageAlt: present.imageAlt || present.name,
      description: present.description,
      mysoftCode: present.codes.mysoft,
    })),
    consentHtml: sanitizeConsentHtml(form.consent),
    isClosed: form.closed,
    noindex: form.noindex,
  }
}

// Contentful's "Jag vill inte ha någon present" card is a selectable present with the key `ingen`;
// Drupal's import gives it `no-present`. Picking it means no present, so it must reach the CRM as
// an empty Premieval, like the skip button.
const NO_PRESENT_KEYS = ['ingen', 'no-present']

export const isNoPresent = (key: string | null | undefined): boolean =>
  !key || NO_PRESENT_KEYS.includes(key)

/**
 * Why a host's Månadsgivare page can't draw this form as Drupal means it, or [] when it can. Each is
 * something neither host has a place for, so the page falls back to Contentful rather than leave it out.
 */
export function monthlyServeGaps(view: MonthlyView): string[] {
  const gaps: string[] = []
  if (!view.sums.length) gaps.push('no amounts (presets)')
  for (const present of view.presents) {
    if (!present.mysoftCode && !isNoPresent(present.key))
      gaps.push(`present without a Mysoft code: ${present.key}`)
  }
  return gaps
}

/**
 * A monthly document → the config `mapMonthlyMembershipFields` builds from a Contentful
 * `interaction` entry. A present's id is its Mysoft code, what the flow sends as `presentId`; the
 * no-present card is dropped, since the flows offer their own skip. Pure.
 */
export function toMonthlyDocument(form: MonthlyDocumentV1): MonthlyMembershipDocument {
  return {
    slug: form.slug,
    title: form.heading,
    subtitle: form.eyebrow,
    landing: formLanding(form),
    sums: [...form.presets],
    minSum: form.minimum ?? undefined,
    recruitmentCode: form.code || undefined,
    presents: form.presents
      .filter((present) => !isNoPresent(present.key))
      .map((present) => ({
        id: present.codes.mysoft,
        title: present.name,
        description: present.description || undefined,
        image: cloudinaryUrl(present.image) || undefined,
        imageAlt: present.imageAlt || present.name,
      })),
    isClosed: form.closed,
    resultSysId: form.results.done ?? undefined,
    postalResultSysId: form.results.pending ?? undefined,
  }
}
